import { useEffect, useState } from 'react';

import AJAX from 'common/AJAX';
import parseAPIResponse from 'common/util/parseAPIResponse';

import type { IntercomTeam } from 'common/api/endpoints/intercom';
import type { Option } from 'common/ui/common/select/SelectCommon';

type GetTeamInboxesResponse = {
  teams: IntercomTeam[];
};

export const useIntercomSourceFilter = (selectedInboxIDs: string[] | null) => {
  const [teamInboxes, setTeamInboxes] = useState<IntercomTeam[] | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let ignoreResult = false;

    // If not loading, we're already fetched the groups, don't do it again
    if (!loading) {
      return;
    }

    const fetchTeamInboxes = async () => {
      const response = await AJAX.post('/api/intercom/getTeamInboxes', {});
      const { error, parsedResponse } = parseAPIResponse<GetTeamInboxesResponse>(response, {
        isSuccessful: (parsedResponse) => {
          return typeof parsedResponse.teams === 'object' && Array.isArray(parsedResponse.teams);
        },
      });
      if (ignoreResult) {
        return;
      }

      if (error || !parsedResponse) {
        setError(true);
      } else {
        const { teams } = parsedResponse;
        setTeamInboxes(teams);
      }
      setLoading(false);
    };

    fetchTeamInboxes();

    return () => {
      ignoreResult = true;
    };
  }, [loading]);

  const options =
    teamInboxes?.map((inbox) => ({
      label: inbox.name,
      value: inbox.id,
    })) ?? [];

  const selectedOptions = teamInboxes
    ?.map((inbox) => {
      const isSelected = selectedInboxIDs?.includes(inbox.id);
      return isSelected ? { label: inbox.name, value: inbox.id } : null;
    })
    .filter(Boolean) as Option[];

  return {
    loading,
    error,
    options,
    selectedOptions,
  };
};
